import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle } from 'lucide-react';
import { MessageModal } from './MessageModal';
interface UploadOCIPassportModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function UploadOCIPassportModal({
  isOpen,
  onClose
}: UploadOCIPassportModalProps) {
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  if (!isOpen) return null;
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileNames = Array.from(files).map(f => f.name);
      setUploadedFiles([...uploadedFiles, ...fileNames]);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSuccessModalOpen(true);
    setTimeout(() => {
      onClose();
      setUploadedFiles([]);
    }, 2000);
  };
  return <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-877">
        <div className="bg-white border border-gray-300 max-w-2xl w-full shadow-lg max-h-[90vh] overflow-y-auto" data-id="element-878">
          <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white" data-id="element-879">
            <h3 className="font-bold text-lg flex items-center gap-2" data-id="element-880">
              <FileText className="w-5 h-5" data-id="element-881" />
              Upload OCI Card / Passport
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-882">
              <X className="w-5 h-5" data-id="element-883" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-884">
            <div className="bg-blue-50 border border-blue-200 p-4" data-id="element-885">
              <h4 className="font-bold text-sm mb-2" data-id="element-886">Required Documents</h4>
              <ul className="text-sm text-gray-700 space-y-1" data-id="element-887">
                <li data-id="element-888">• OCI Card (front and back) OR Passport (photo page)</li>
                <li data-id="element-889">• Clear, readable scans or photos</li>
                <li data-id="element-890">• Accepted formats: PDF, JPG, PNG (max 10MB each)</li>
              </ul>
            </div>

            <div data-id="element-891">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-892">
                Document Type *
              </label>
              <select required className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-893">
                <option value="" data-id="element-894">Select Document Type</option>
                <option value="oci" data-id="element-895">OCI Card</option>
                <option value="passport" data-id="element-896">Passport</option>
              </select>
            </div>

            <div data-id="element-897">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-898">
                Upload Documents *
              </label>
              <div className="border-2 border-dashed border-gray-300 p-8 bg-gray-50 text-center" data-id="element-899">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-3" data-id="element-900" />
                <p className="text-sm font-bold mb-2" data-id="element-901">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mb-4" data-id="element-902">
                  PDF, JPG, PNG (max 10MB each)
                </p>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" id="oci-upload" data-id="element-903" />
                <label htmlFor="oci-upload" className="inline-block px-6 py-2 bg-white border border-gray-300 text-sm font-bold cursor-pointer hover:bg-gray-50" data-id="element-904">
                  Choose Files
                </label>
              </div>

              {uploadedFiles.length > 0 && <div className="mt-4 space-y-2" data-id="element-905">
                  {uploadedFiles.map((file, i) => <div key={i} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200" data-id="element-906">
                      <CheckCircle className="w-4 h-4 text-green-600" data-id="element-907" />
                      <span className="text-sm flex-1" data-id="element-908">{file}</span>
                      <button type="button" onClick={() => setUploadedFiles(uploadedFiles.filter((_, idx) => idx !== i))} className="text-xs text-red-600 hover:underline" data-id="element-909">
                        Remove
                      </button>
                    </div>)}
                </div>}
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4" data-id="element-910">
              <label className="flex items-start gap-3" data-id="element-911">
                <input type="checkbox" required className="mt-1 w-4 h-4 accent-[#1f2933]" data-id="element-912" />
                <span className="text-xs text-gray-700" data-id="element-913">
                  I certify that the uploaded documents are authentic and belong
                  to me. I understand that providing false information may
                  result in account suspension.
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-914">
              <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-915">
                Cancel
              </button>
              <button type="submit" disabled={uploadedFiles.length === 0} className="px-6 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-916">
                Submit for Verification
              </button>
            </div>
          </form>
        </div>
      </div>

      <MessageModal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} title="Documents Submitted" message="Your OCI/Passport documents have been submitted for verification. You'll receive a notification within 24-48 hours." type="success" data-id="element-917" />
    </>;
}